import { initializeBlankStep } from './workflow-step-composition';
import type { BusinessCalendar } from '../business-calendars/platform-business-calendars.models';
import type {
  WorkflowCapabilityReference,
  WorkflowDefinitionDocument,
  WorkflowStepTimer,
  WorkflowTimerAction,
} from './platform-workflow-definition-lifecycle.models';
import {
  automationOperation,
  type WorkflowAutomationCatalog,
} from './workflow-automation-authoring';
import {
  workflowTimerAuthoringSnapshot,
  type WorkflowTimerAuthoringSnapshot,
} from './workflow-timer-catalog.models';
import {
  timerActionError,
  timerMaximumDays,
  timerMaximumMinutes,
  timerRuleError,
} from './workflow-step-timers-authoring';

interface WorkflowTimerDeadlineFields extends Omit<
  WorkflowStepTimer,
  | 'version'
  | 'calendar'
  | 'dueLocalTime'
  | 'gapCode'
  | 'overlapCode'
  | 'warningLeadMinutes'
  | 'warningAction'
  | 'breachAction'
> {
  calendar?: WorkflowStepTimer['calendar'];
  dueLocalTime?: string | null;
  gapCode?: string | null;
  overlapCode?: string | null;
}
export interface WorkflowStepTimerV1 extends WorkflowTimerDeadlineFields {
  version: 1;
  warningLeadMinutes: number;
  warningAction: WorkflowTimerAction;
  breachAction: WorkflowTimerAction;
}
export interface WorkflowStepTimerV2 extends WorkflowTimerDeadlineFields {
  version: 2;
  warningLeadMinutes?: number | null;
  warningAction?: WorkflowTimerAction | null;
  breachAction?: WorkflowTimerAction | null;
}
export type WorkflowVersionedStepTimer =
  | WorkflowStepTimerV1
  | WorkflowStepTimerV2;

/** Input slots remain incomplete until the legacy candidate has been explicitly validated. */
export type WorkflowStepTimerV1Draft = Omit<
  WorkflowStepTimerV1,
  'durationAmount' | 'warningLeadMinutes' | 'warningAction' | 'breachAction'
> & {
  durationAmount: number | null;
  warningLeadMinutes: number | null;
  warningAction?: WorkflowTimerAction | null;
  breachAction?: WorkflowTimerAction | null;
};
export type SavedStepTimerRow = {
  index: number;
  id: string | null;
  label: string | null;
  raw: unknown;
} & (
  | { kind: 'v1'; timer: WorkflowStepTimerV1 }
  | { kind: 'v2'; timer: WorkflowStepTimerV2 }
  | { kind: 'retained' }
);

/** Captured by the future private catalog owner, independently of the returned snapshot. */
export interface WorkflowTimerCatalogRequest {
  workflowCode: string;
  packReference: WorkflowDefinitionDocument['packReference'];
  sourceContext: string;
  owner: string;
  namespace: string;
  generation: number;
}
export interface WorkflowTimerProposalAvailability {
  catalog: WorkflowAutomationCatalog | null;
  calendars: readonly BusinessCalendar[];
  lease?: { request: WorkflowTimerCatalogRequest; snapshot: unknown } | null;
  currentRequest?: WorkflowTimerCatalogRequest | null;
}
export interface WorkflowTimerProposalTarget {
  nodeId: string;
  /** Null is an explicit Add; an existing row requires both its saved index and ID. */
  timerIndex: number | null;
  timerId: string | null;
  originalTimers: unknown;
}
export type WorkflowTimerProposalChange =
  | { kind: 'upsert'; timer: WorkflowVersionedStepTimer }
  | { kind: 'remove' };
/** Raw siblings/pins remain opaque; this is deliberately not a full authoring document. */
export interface WorkflowTimerChangeProposal {
  nodeId: string;
  sourceContext: string;
  originalTimers: unknown;
  timer: WorkflowVersionedStepTimer | null;
  timers: readonly unknown[];
  capabilityReferences: readonly unknown[] | null | undefined;
  addedCapabilityReferences: readonly WorkflowCapabilityReference[];
  requiredPayloadVersion: string;
}

const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const code = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.length <= 120 &&
  /^[a-z][a-z0-9]*(?:[.-][a-z0-9]+)*$/.test(value);
const timerFields = new Set([
  'version',
  'id',
  'label',
  'startCode',
  'durationUnit',
  'durationAmount',
  'calendar',
  'dueLocalTime',
  'gapCode',
  'overlapCode',
  'pauseCode',
  'resetCode',
  'reentryCode',
  'warningLeadMinutes',
  'warningAction',
  'breachAction',
]);
function reference(value: unknown): value is WorkflowCapabilityReference {
  if (!record(value)) return false;
  const key = value['key'];
  return (
    record(key) &&
    ['provider', 'kind', 'code'].every(
      (name) => typeof key[name] === 'string',
    ) &&
    Number.isSafeInteger(key['version']) &&
    Number(key['version']) > 0 &&
    ['inputSchemaHash', 'outputSchemaHash', 'contractHash'].every(
      (name) => typeof value[name] === 'string',
    )
  );
}
const action = (value: unknown): value is WorkflowTimerAction =>
  record(value) &&
  Object.keys(value).every((key) =>
    [
      'id',
      'typeCode',
      'command',
      'inputs',
      'outcomes',
      'group',
      'order',
      'mode',
      'failurePolicy',
      'caller',
      'requiredForProgression',
    ].includes(key),
  ) &&
  ['id', 'group', 'typeCode', 'mode', 'failurePolicy', 'caller'].every(
    (key) => typeof value[key] === 'string',
  ) &&
  typeof value['order'] === 'number' &&
  typeof value['requiredForProgression'] === 'boolean' &&
  reference(value['command']) &&
  Array.isArray(value['inputs']) &&
  value['inputs'].every(
    (input) =>
      record(input) &&
      typeof input['field'] === 'string' &&
      typeof input['source'] === 'string',
  ) &&
  Array.isArray(value['outcomes']) &&
  value['outcomes'].every((item) => typeof item === 'string');
const same = (left: unknown, right: unknown): boolean =>
  JSON.stringify(left) === JSON.stringify(right);

/** Structural admission only; unchanged raw rows need no new installed metadata. */
export function isKnownStepTimer(
  value: unknown,
): value is WorkflowVersionedStepTimer {
  const calendar = record(value) ? value['calendar'] : undefined;
  if (
    !record(value) ||
    Object.keys(value).some((key) => !timerFields.has(key)) ||
    (value['version'] !== 1 && value['version'] !== 2) ||
    ![
      'id',
      'label',
      'startCode',
      'durationUnit',
      'pauseCode',
      'resetCode',
      'reentryCode',
    ].every((key) => typeof value[key] === 'string') ||
    typeof value['durationAmount'] !== 'number' ||
    !['dueLocalTime', 'gapCode', 'overlapCode'].every(
      (key) => value[key] == null || typeof value[key] === 'string',
    ) ||
    !(
      calendar == null ||
      (record(calendar) &&
        Object.keys(calendar).every((key) =>
          ['code', 'versionCode', 'contentHash'].includes(key),
        ) &&
        ['code', 'versionCode', 'contentHash'].every(
          (key) => typeof calendar[key] === 'string',
        ))
    )
  )
    return false;
  return value['version'] === 1
    ? typeof value['warningLeadMinutes'] === 'number' &&
        action(value['warningAction']) &&
        action(value['breachAction'])
    : (value['warningLeadMinutes'] == null ||
        typeof value['warningLeadMinutes'] === 'number') &&
        (value['warningAction'] == null || action(value['warningAction'])) &&
        (value['breachAction'] == null || action(value['breachAction']));
}

/** Read only: original row identity/order is retained, never filtered into a Timer1 list. */
export function savedStepTimerRows(
  value: unknown,
): readonly SavedStepTimerRow[] | null {
  if (value == null) return [];
  if (!Array.isArray(value)) return null;
  const rows: readonly unknown[] = value;
  return rows.map((raw, index): SavedStepTimerRow => {
    const id = record(raw) && typeof raw['id'] === 'string' ? raw['id'] : null;
    const label =
      record(raw) && typeof raw['label'] === 'string' ? raw['label'] : null;
    if (!isKnownStepTimer(raw))
      return { index, id, label, raw, kind: 'retained' };
    return raw.version === 1
      ? { index, id, label, raw, kind: 'v1', timer: raw }
      : { index, id, label, raw, kind: 'v2', timer: raw };
  });
}

/** A legacy editor can repair nullable numeric/stage inputs without claiming they are complete wire settings. */
export function isLegacyStepTimerDraft(
  value: unknown,
): value is WorkflowStepTimerV1Draft {
  if (
    !record(value) ||
    value['version'] !== 1 ||
    Object.keys(value).some((key) => !timerFields.has(key)) ||
    ![
      'id',
      'label',
      'startCode',
      'durationUnit',
      'pauseCode',
      'resetCode',
      'reentryCode',
    ].every((key) => typeof value[key] === 'string') ||
    !['durationAmount', 'warningLeadMinutes'].every(
      (key) => value[key] === null || typeof value[key] === 'number',
    ) ||
    !['dueLocalTime', 'gapCode', 'overlapCode'].every(
      (key) => value[key] == null || typeof value[key] === 'string',
    ) ||
    !['warningAction', 'breachAction'].every(
      (key) => value[key] == null || action(value[key]),
    )
  )
    return false;
  const calendar = value['calendar'];
  return (
    calendar == null ||
    (record(calendar) &&
      Object.keys(calendar).every((key) =>
        ['code', 'versionCode', 'contentHash'].includes(key),
      ) &&
      ['code', 'versionCode', 'contentHash'].every(
        (key) => typeof calendar[key] === 'string',
      ))
  );
}

/** Version2 has a mandatory deadline, an optional complete warning pair and an independent overdue action. */
export function timerV2RuleError(
  timer: WorkflowStepTimerV2,
  catalog: WorkflowAutomationCatalog,
  calendars: readonly BusinessCalendar[],
  document: WorkflowDefinitionDocument,
  nodeId: string,
): string | null {
  if (!isKnownStepTimer(timer) || timer.version !== 2 || !code(timer.id))
    return 'This saved timer contract is unavailable. Its original configuration is preserved.';
  if (
    !timer.label.trim() ||
    timer.label !== timer.label.trim() ||
    timer.label.length > 160
  )
    return 'Enter a timer name of up to 160 characters.';
  if (
    timer.startCode !== 'on_entry' ||
    timer.reentryCode !== 'restart_new_occurrence' ||
    !['disabled', 'manual_before_warning'].includes(timer.pauseCode) ||
    !['disabled', 'restart_from_now'].includes(timer.resetCode)
  )
    return 'Choose the timer start, pause, reset and fresh-visit rules.';
  if (
    !['elapsed_minutes', 'calendar_days', 'working_days'].includes(
      timer.durationUnit,
    ) ||
    !Number.isInteger(timer.durationAmount) ||
    timer.durationAmount < 1 ||
    timer.durationAmount >
      (timer.durationUnit === 'elapsed_minutes'
        ? timerMaximumMinutes
        : timerMaximumDays)
  )
    return 'Choose a duration unit and a whole-number amount within the supported range.';
  const warning = timer.warningAction != null;
  if (warning !== (timer.warningLeadMinutes != null))
    return 'A warning needs both its lead time and operation; turn both off together.';
  if (
    warning &&
    (!Number.isInteger(timer.warningLeadMinutes) ||
      Number(timer.warningLeadMinutes) < 1 ||
      Number(timer.warningLeadMinutes) > timerMaximumMinutes)
  )
    return 'Enter the warning lead in whole minutes from 1 to 5,270,400.';
  if (!warning && timer.pauseCode === 'manual_before_warning')
    return 'Manual pause before warning is unavailable while Warning is Off.';
  if (timer.durationUnit === 'elapsed_minutes') {
    if (
      timer.calendar != null ||
      timer.dueLocalTime != null ||
      timer.gapCode != null ||
      timer.overlapCode != null
    )
      return 'Elapsed-minute timers cannot carry calendar or local-time settings.';
    if (warning && Number(timer.warningLeadMinutes) > timer.durationAmount)
      return 'The warning must not fall before the timer starts.';
  } else {
    const calendar = timer.calendar;
    if (
      !calendar ||
      !code(calendar.code) ||
      !calendar.versionCode.trim() ||
      calendar.versionCode !== calendar.versionCode.trim() ||
      calendar.versionCode.length > 40 ||
      !/^[a-f0-9]{64}$/.test(calendar.contentHash) ||
      calendars.filter(
        (item) =>
          item?.code === calendar.code &&
          item.versionCode === calendar.versionCode &&
          item.contentHash === calendar.contentHash,
      ).length !== 1
    )
      return 'Choose one available published calendar version.';
    if (
      !timer.dueLocalTime ||
      !/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(timer.dueLocalTime) ||
      timer.gapCode !== 'reject' ||
      !['reject', 'earlier', 'later'].includes(timer.overlapCode ?? '')
    )
      return 'Choose the local due time and explicit daylight-saving rules.';
  }
  for (const stage of [timer.warningAction, timer.breachAction]) {
    if (stage == null) continue;
    const error = timerActionError(stage, catalog, document, nodeId);
    if (error) return error;
  }
  return null;
}

function requireV2Availability(
  document: WorkflowDefinitionDocument,
  availability: WorkflowTimerProposalAvailability,
): WorkflowTimerAuthoringSnapshot {
  const request = availability.lease?.request,
    current = availability.currentRequest;
  const snapshot = availability.lease?.snapshot;
  const pack: unknown = document.packReference;
  if (
    !request ||
    !current ||
    !workflowTimerAuthoringSnapshot(snapshot) ||
    typeof request.owner !== 'string' ||
    !request.owner.trim() ||
    typeof request.namespace !== 'string' ||
    !request.namespace.trim() ||
    !Number.isSafeInteger(request.generation) ||
    request.generation < 0 ||
    request.workflowCode !== document.workflowCode ||
    request.sourceContext !== JSON.stringify(document) ||
    (pack != null &&
      (!record(pack) ||
        Object.keys(pack).some(
          (key) => !['code', 'version', 'manifestHash'].includes(key),
        ) ||
        !code(pack['code']) ||
        !Number.isSafeInteger(pack['version']) ||
        Number(pack['version']) < 1 ||
        typeof pack['manifestHash'] !== 'string' ||
        !/^[a-f0-9]{64}$/.test(pack['manifestHash']))) ||
    !same(request.packReference ?? null, document.packReference ?? null) ||
    ![
      'workflowCode',
      'packReference',
      'sourceContext',
      'owner',
      'namespace',
      'generation',
    ].every((key) => {
      if (key === 'workflowCode')
        return request.workflowCode === current.workflowCode;
      if (key === 'packReference')
        return same(
          request.packReference ?? null,
          current.packReference ?? null,
        );
      if (key === 'sourceContext')
        return request.sourceContext === current.sourceContext;
      if (key === 'owner') return request.owner === current.owner;
      if (key === 'namespace') return request.namespace === current.namespace;
      return request.generation === current.generation;
    }) ||
    !snapshot.runtime.producerEnabled ||
    !snapshot.runtime.optionalStagesEnabled ||
    !snapshot.runtime.optionalFormatExecutable
  )
    throw new Error(
      'Reload current timer availability for this exact workflow, pack, source and author before changing an optional-stage timer.',
    );
  return snapshot;
}

function canonicalV2(timer: WorkflowStepTimerV2): WorkflowStepTimerV2 {
  const { warningLeadMinutes, warningAction, breachAction, ...deadline } =
    timer;
  return {
    ...structuredClone(deadline),
    ...(warningAction != null && typeof warningLeadMinutes === 'number'
      ? { warningLeadMinutes, warningAction: structuredClone(warningAction) }
      : {}),
    ...(breachAction != null
      ? { breachAction: structuredClone(breachAction) }
      : {}),
  };
}
const identity = (pin: unknown): string | null =>
  record(pin) && record(pin['key'])
    ? JSON.stringify([
        pin['key']['provider'],
        pin['key']['kind'],
        pin['key']['code'],
        pin['key']['version'],
      ])
    : null;
const sameReference = (
  pin: unknown,
  expected: WorkflowCapabilityReference,
): boolean =>
  record(pin) &&
  identity(pin) === identity(expected) &&
  pin['inputSchemaHash'] === expected.inputSchemaHash &&
  pin['outputSchemaHash'] === expected.outputSchemaHash &&
  pin['contractHash'] === expected.contractHash;

/** Returns null for an exact no-op, before availability, initialization, pins or payload-floor work. */
export function proposeStepTimerChange(
  document: WorkflowDefinitionDocument,
  target: WorkflowTimerProposalTarget,
  change: WorkflowTimerProposalChange,
  availability: WorkflowTimerProposalAvailability,
): WorkflowTimerChangeProposal | null {
  if (change.kind !== 'upsert' && change.kind !== 'remove')
    throw new Error('Choose an explicit timer change.');
  if (!Array.isArray(document.nodes))
    throw new Error('Select one exact work step.');
  const nodes = document.nodes.filter((node) => node?.id === target.nodeId);
  if (nodes.length !== 1 || nodes[0].typeCode !== 'human_step')
    throw new Error('Select one exact work step.');
  const declaration: unknown = nodes[0].declaration;
  if (declaration != null && !record(declaration))
    throw new Error(
      'The saved step declaration is unavailable; its original content is preserved.',
    );
  const behavior = record(declaration) ? declaration['behavior'] : undefined;
  if (behavior != null && !record(behavior))
    throw new Error(
      'The saved step behavior is unavailable; its original content is preserved.',
    );
  const saved = record(behavior) ? behavior['timers'] : undefined;
  if (!same(saved, target.originalTimers))
    throw new Error(
      'The saved timer baseline changed. Reopen this exact timer before applying.',
    );
  if (saved != null && !Array.isArray(saved))
    throw new Error(
      'The saved timer container is unavailable; its original content is preserved.',
    );
  const rows: unknown[] = saved ?? [];
  const index = target.timerIndex;
  const adding = index === null;
  const previous = index === null ? undefined : rows[index];
  if (
    adding
      ? target.timerId !== null || change.kind !== 'upsert'
      : !Number.isInteger(target.timerIndex) ||
        Number(target.timerIndex) < 0 ||
        Number(target.timerIndex) >= rows.length ||
        !record(previous) ||
        previous['id'] !== target.timerId ||
        !code(target.timerId)
  )
    throw new Error('Select the exact saved timer index and identity.');
  if (change.kind === 'upsert' && !adding && same(previous, change.timer))
    return null;
  if (
    !adding &&
    rows.filter((row) => record(row) && row['id'] === target.timerId).length !==
      1
  )
    throw new Error(
      'The saved timer identity is ambiguous. Preserve the original rows for review.',
    );
  const legacyRepair =
    isLegacyStepTimerDraft(previous) &&
    (change.kind === 'remove' || change.timer.version === 1);
  if (!adding && !isKnownStepTimer(previous) && !legacyRepair)
    throw new Error(
      'This saved timer is read-only; its original content is preserved.',
    );
  if (
    change.kind === 'upsert' &&
    (!isKnownStepTimer(change.timer) ||
      (!adding && change.timer.id !== target.timerId))
  )
    throw new Error(
      'Use a known timer contract without changing its saved identity.',
    );
  // Removal is exact-source repair, introducing no stages, pins or format floor.
  const v2 = change.kind === 'upsert' && change.timer.version === 2;
  const catalog = v2
    ? requireV2Availability(document, availability).catalog
    : availability.catalog;
  if (change.kind === 'upsert' && (!catalog || catalog.contractVersion !== 1))
    throw new Error('Reload installed timer operations before applying.');
  const timer = change.kind === 'remove' ? null : change.timer;
  if (timer && catalog) {
    const error =
      timer.version === 2
        ? timerV2RuleError(
            timer,
            catalog,
            availability.calendars,
            document,
            target.nodeId,
          )
        : timerRuleError(
            {
              ...timer,
              calendar: timer.calendar ?? null,
              dueLocalTime: timer.dueLocalTime ?? null,
              gapCode: timer.gapCode ?? null,
              overlapCode: timer.overlapCode ?? null,
            },
            catalog,
            availability.calendars,
            document,
            target.nodeId,
          );
    if (error) throw new Error(error);
  }
  const next: unknown[] = [...rows];
  const candidate =
    timer?.version === 2
      ? canonicalV2(timer)
      : timer == null
        ? null
        : structuredClone(timer);
  if (adding) next.push(candidate);
  else if (candidate == null) next.splice(Number(target.timerIndex), 1);
  else next[Number(target.timerIndex)] = candidate;
  if (next.length > 16)
    throw new Error('A work step supports at most 16 timers.');
  const ids = new Set<string>(),
    stages = new Set<string>();
  for (const row of next) {
    if (!record(row)) continue;
    if (typeof row['id'] === 'string') {
      if (ids.has(row['id']))
        throw new Error('Each saved timer needs a distinct identity.');
      ids.add(row['id']);
    }
    for (const value of [row['warningAction'], row['breachAction']]) {
      if (!record(value) || typeof value['id'] !== 'string') continue;
      if (stages.has(value['id']))
        throw new Error('Each timer stage needs a distinct action identity.');
      stages.add(value['id']);
    }
  }
  const rawPins: unknown = document.capabilityReferences;
  if (rawPins != null && !Array.isArray(rawPins))
    throw new Error(
      'The saved capability pins are unavailable; preserve them before editing timers.',
    );
  const pins: unknown[] = [...(rawPins ?? [])],
    added: WorkflowCapabilityReference[] = [];
  if (candidate && catalog)
    for (const stage of [candidate.warningAction, candidate.breachAction]) {
      if (stage == null) continue;
      const operation = automationOperation(catalog, stage);
      if (!operation)
        throw new Error('The exact installed timer operation is unavailable.');
      for (const pin of [
        operation.command,
        ...operation.requiredCapabilities,
      ]) {
        const matches = pins.filter(
          (savedPin) => identity(savedPin) === identity(pin),
        );
        if (
          matches.length > 1 ||
          matches.some((savedPin) => !sameReference(savedPin, pin))
        )
          throw new Error(
            'A saved operation pin has a different exact contract. Repair it before applying the timer.',
          );
        if (!matches.length) {
          pins.push(structuredClone(pin));
          added.push(structuredClone(pin));
        }
      }
    }
  if (pins.length > 100)
    throw new Error('A workflow supports at most 100 capability references.');
  const versions = Array.from({ length: 23 }, (_, index) => `1.${index}`);
  const current = versions.indexOf(document.payloadSchemaVersion);
  if (current < 0)
    throw new Error(
      'This workflow format is unavailable for timer changes; its original content is preserved.',
    );
  const minimum = timer == null ? current : timer.version === 2 ? 22 : 16;
  return {
    nodeId: target.nodeId,
    sourceContext: JSON.stringify(document),
    originalTimers: structuredClone(saved),
    timer: candidate,
    timers: structuredClone(next),
    capabilityReferences: added.length ? pins : structuredClone(rawPins),
    addedCapabilityReferences: added,
    requiredPayloadVersion: versions[Math.max(current, minimum)],
  };
}

/** Compose the existing narrow proposal into the one saved document; no raw row becomes a Timer1 assertion. */
export function applyStepTimerChange(
  document: WorkflowDefinitionDocument,
  target: WorkflowTimerProposalTarget,
  change: WorkflowTimerProposalChange,
  availability: WorkflowTimerProposalAvailability,
): WorkflowDefinitionDocument {
  const proposal = proposeStepTimerChange(
    document,
    target,
    change,
    availability,
  );
  if (!proposal) return document;
  const selected = document.nodes.find((node) => node.id === target.nodeId)!;
  if (
    selected.declaration != null &&
    (!record(selected.declaration.behavior) ||
      !record(selected.declaration.detail))
  )
    throw new Error(
      'The saved step declaration is unavailable; its original content is preserved.',
    );
  const initialized =
    selected.declaration == null
      ? initializeBlankStep(document, target.nodeId)
      : document;
  return {
    ...initialized,
    payloadSchemaVersion: proposal.requiredPayloadVersion,
    ...(proposal.addedCapabilityReferences.length
      ? {
          capabilityReferences: [
            ...(document.capabilityReferences ?? []),
            ...structuredClone(proposal.addedCapabilityReferences),
          ],
        }
      : {}),
    nodes: initialized.nodes.map((node) =>
      node.id !== target.nodeId
        ? node
        : {
            ...node,
            declaration: {
              ...node.declaration!,
              behavior: {
                ...node.declaration!.behavior,
                timers: [...proposal.timers],
              },
            },
          },
    ),
  };
}
